/* CUSTOM_RFQPORTAL_BY_CLAUDE — Universal Lead Capture: storefront form interceptor (Phase 2)
 *
 * Hooks every Shopify {% form 'contact' %} submission on the storefront and
 * mirrors the data to the rfq-worker /lead-intake endpoint, where it becomes
 * a NEW LEAD on the Quote Dashboard with capacity-aware rep assignment.
 *
 * Phase 2 redundancy gate: this is fire-and-forget via navigator.sendBeacon,
 * so the native Shopify form submit ALWAYS proceeds normally. The legacy
 * email to info@ keeps firing as a paranoia backup until Phase 4 retires it.
 *
 * Source determination — derived from URL path (more reliable than form
 * subject, which can contain "Bulk" on vertical pages and trip false matches).
 *   /pages/request-bulk-quote               → bulk-quote
 *   /pages/contact                          → contact-form
 *   /pages/request-sample[*]                → sample-request (rare via this path)
 *   /pages/rubber-flooring-*                → vertical-form (the 15 LPs)
 *   anything else with a contact form       → contact-form (safe default)
 *
 * Loaded once per page via contact-form.liquid. Multiple forms per page
 * (rare — newsletter is a customer form, not a contact form, so it's safe)
 * all share this single submit listener attached to document.
 */
(function() {
  'use strict';

  var WORKER_URL = 'https://rfq-worker.ryan-29b.workers.dev/lead-intake';

  // Already-installed guard so multiple contact-form section instances on
  // the same page don't double-bind.
  if (window.__bsfsLeadInterceptorInstalled) return;
  window.__bsfsLeadInterceptorInstalled = true;

  // Diagnostic logging — gated behind window.__bsfsDebug so production
  // visitors don't see [lead-capture] / [turnstile] payload internals in
  // their browser console. To re-enable during testing, run in DevTools:
  //   window.__bsfsDebug = true
  // or append ?bsfs_debug=1 to the URL.
  var DEBUG = false;
  try {
    if (window.__bsfsDebug === true) DEBUG = true;
    else if (typeof URLSearchParams !== 'undefined' && new URLSearchParams(window.location.search).get('bsfs_debug') === '1') {
      window.__bsfsDebug = true;
      DEBUG = true;
    }
  } catch (e) {}
  function debug() {
    if (!DEBUG) return;
    try { console.log.apply(console, arguments); } catch (e) {}
  }
  function debugWarn() {
    if (!DEBUG) return;
    try { console.warn.apply(console, arguments); } catch (e) {}
  }

  /* CUSTOM_FORM_SUBMIT_OVERLAY_BY_CLAUDE — Block programmatic form.submit()
     while our intercept flow is in progress. Confirmed via Ryan's 2026-05-18
     diagnostic test: e.preventDefault() runs successfully (defaultPrevented=
     true on a verified post-handler listener) AND no second submit event
     fires, yet Shopify still receives a /contact POST and returns 302 to
     /challenge. The only way to POST a form without firing a submit event
     is form.submit() programmatically. Most likely culprit: Cloudflare
     Turnstile's auto-submit-on-success behavior, which uses form.submit()
     to bypass event listeners (intentionally — so existing site code can't
     accidentally cancel its own anti-bot flow).
     We patch HTMLFormElement.prototype.submit globally and only block when
     our dataset.bsfsLeadIntakeFired flag is set on the target form. Other
     theme code that legitimately calls form.submit() (custom-freight-
     notice.js for cart-drawer-form, customer-addresses.js, speech-search.
     js) keeps working because the flag is contact-form-specific. */
  (function patchFormSubmit() {
    try {
      var origSubmit = HTMLFormElement.prototype.submit;
      HTMLFormElement.prototype.submit = function () {
        if (this && this.dataset && this.dataset.bsfsLeadIntakeFired === 'true') {
          debugWarn('[lead-capture] Blocking form.submit() during in-flight intercept. Caller stack:', new Error().stack);
          return;
        }
        return origSubmit.apply(this, arguments);
      };
    } catch (e) {
      debugWarn('[lead-capture] Could not patch form.submit():', e);
    }
  })();

  function deriveSource(sourceUrl) {
    // URL-based — reliable. Form subject is set per-page via a hidden field
    // and can contain 'Bulk' on vertical landing pages, which broke a previous
    // subject-based heuristic.
    var path = '';
    try { path = new URL(sourceUrl || window.location.href).pathname.toLowerCase(); }
    catch (e) { path = (window.location.pathname || '').toLowerCase(); }
    if (path.indexOf('/pages/request-bulk-quote') === 0) return 'bulk-quote';
    if (path.indexOf('/pages/contact') === 0) return 'contact-form';
    if (path.indexOf('/pages/request-sample') === 0) return 'sample-request';
    if (path.indexOf('/pages/rubber-flooring-') === 0) return 'vertical-form';
    // CUSTOM_BLOG_CTA_BY_CLAUDE — inline blog-article CTAs submit ON the post, so
    // their sourceUrl is /blogs/...; tag them 'blog-cta' for clean segmentation. The
    // worker /lead-intake allowlist now includes 'blog-cta' (added 2026-06-21).
    // (Contact-form submits reached via a link FROM a post stay 'contact-form' here —
    // they're attributed to the blog by referrer, not source, in /blog-lead-stats.)
    if (path.indexOf('/blogs/') === 0) return 'blog-cta';
    return 'contact-form'; // safe default for any other host page
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE — UTM reads from site-wide journey
   * (utm-journey-tracker.js bsfs_utm_journey localStorage array), not the
   * old per-tab sessionStorage. That sessionStorage capture only fired at
   * submit time which lost any user who landed on a UTM URL, browsed
   * around, and converted later — first-touch attribution for paid Tuesday
   * → Friday-return-visit was previously zero.
   *
   * Returns:
   *   utm: { source, medium, campaign, term, content }  — LAST-TOUCH
   *        summary, kept in this shape for backward-compat with the dashboard
   *        + lead-notification email which already read these keys.
   *   journey: [ ...full array... ]  — passed through to the worker for
   *        server-side multi-touch attribution at conversion time.
   */
  function readUtmJourney() {
    var journey = [];
    try {
      var raw = localStorage.getItem('bsfs_utm_journey');
      if (raw) {
        var parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) journey = parsed;
      }
    } catch (e) {}

    // If the current URL has UTM params but the tracker script hasn't run
    // yet (rare race — defer ordering), capture them ourselves so we don't
    // lose this submission's attribution.
    try {
      var url = new URL(window.location.href);
      var paramKeys = ['utm_source','utm_medium','utm_campaign','utm_term','utm_content',
                       'gclid','fbclid','msclkid','ttclid','li_fat_id'];
      var here = {};
      var any = false;
      paramKeys.forEach(function (k) {
        var v = url.searchParams.get(k);
        if (v) { here[k] = v; any = true; }
      });
      if (any) {
        var lastEntry = journey[journey.length - 1];
        var hereSig = (here.utm_source||'') + '|' + (here.utm_medium||'') + '|' + (here.utm_campaign||'');
        var lastSig = lastEntry
          ? ((lastEntry.utm_source||'') + '|' + (lastEntry.utm_medium||'') + '|' + (lastEntry.utm_campaign||''))
          : '';
        if (hereSig !== lastSig) {
          here.ts = new Date().toISOString();
          here.referrer = (document.referrer || '').slice(0, 500);
          here.landingPath = (window.location.pathname + window.location.search).slice(0, 300);
          journey.push(here);
        }
      }
    } catch (e) {}

    var lastTouch = journey[journey.length - 1] || {};
    var utm = {};
    if (lastTouch.utm_source)   utm.source   = lastTouch.utm_source;
    if (lastTouch.utm_medium)   utm.medium   = lastTouch.utm_medium;
    if (lastTouch.utm_campaign) utm.campaign = lastTouch.utm_campaign;
    if (lastTouch.utm_term)     utm.term     = lastTouch.utm_term;
    if (lastTouch.utm_content)  utm.content  = lastTouch.utm_content;

    return { utm: utm, journey: journey };
  }

  // GA4 client_id from the `_ga` cookie. Format: GA1.X.<id>.<ts>. Strip the
  // first two dot-separated segments; what's left is the cross-device user
  // pseudo ID we can later JOIN against the GA4 BigQuery export. Tolerates
  // future GA1.x variants (Google has rev'd this format silently before).
  function getGAClientId() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
      if (!m) return null;
      var raw = decodeURIComponent(m[1]);
      var parts = raw.split('.');
      if (parts.length < 4) return null;
      return parts.slice(2).join('.');
    } catch (e) { return null; }
  }

  // SHA-256 hex of (lowercase, trimmed) email — cross-device stitching key.
  // No PII leaves the browser; the hash is one-way and matches what server
  // side will hash for identity:{emailHash} lookups in Phase 5+.
  async function hashEmail(email) {
    try {
      if (!email || !crypto || !crypto.subtle) return null;
      var enc = new TextEncoder().encode(String(email).trim().toLowerCase());
      var buf = await crypto.subtle.digest('SHA-256', enc);
      var arr = Array.from(new Uint8Array(buf));
      return arr.map(function (b) { return b.toString(16).padStart(2, '0'); }).join('');
    } catch (e) { return null; }
  }
  /* END CUSTOM_MARKETINGOPS_BY_CLAUDE */

  // Map a contact[FieldName] form-input name to its inner field name.
  // Returns null for non-contact-namespaced fields.
  function unwrapName(name) {
    if (!name) return null;
    var m = name.match(/^contact\[(.+)\]$/);
    return m ? m[1] : null;
  }

  // Known field-name aliases (case-insensitive) → canonical lead-intake fields.
  // Storefront uses translation strings for Name/Phone/Message so the actual
  // key can vary by locale; match case-insensitively against the lowercase form.
  // contact[location] is handled SPECIALLY in buildPayload (not via this map)
  // because it carries city+state+zip combined ("Broomfield, CO 80021"); we
  // parse it apart so the lead record gets clean state (drives territory
  // assignment in the worker) + zip + city separately.
  var ALIASES = {
    name: 'name',
    email: 'email',
    phone: 'phone',
    message: 'message',
    body: 'message',                  // Shopify's default contact message field is named "body"
    entity_name: 'organization',
    customer_type: 'residentialOrCommercial',
    inquiry_type: 'inquiryType',      // value is normalized — see normalizeInquiry() (form sets a marketing string, schema needs enum)
    'form subject': null,             // consumed for source derivation, not a lead field
    'source page': null,              // consumed for sourceUrl, not a lead field
    pages_viewed: 'pagesViewed',      // legacy email + new email both surface it; reps love seeing browsing journey
    attachments: 'attachments',
    order_number: 'orderNumber',
    location: null,                   // handled separately — see parseLocation
  };

  // Parse the theme's contact[location] hidden field, populated by the
  // zippopotam.us lookup as either "City, ST ZIP" (success) or
  // "Unknown - ZIP" (lookup failed) or empty. Extracts state, zip, city
  // into separate canonical fields so the worker's territory matcher works.
  function parseLocation(locVal) {
    if (!locVal) return {};
    var trimmed = String(locVal).trim();
    // "City, ST ZIP" — most common format from a successful lookup
    var m = trimmed.match(/^(.+?),\s*([A-Za-z]{2})\s+(\d{5})$/);
    if (m) return { city: m[1].trim(), state: m[2].toUpperCase(), zip: m[3] };
    // "Unknown - ZIP" — lookup failed but we still have a zip
    var m2 = trimmed.match(/(\d{5})\s*$/);
    if (m2) return { zip: m2[1] };
    return {};
  }

  function buildPayload(form) {
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE — utm/journey + gaClientId. emailHash is
       async so it's filled in by the submit handler after buildPayload returns. */
    var uj = readUtmJourney();
    var payload = {
      sourceUrl: window.location.href,
      formId: form.id || '',
      formName: '',
      responses: {},
      utm: uj.utm,
      journey: uj.journey,
      gaClientId: getGAClientId(),
      referrer: document.referrer || '',
    };
    /* END CUSTOM_MARKETINGOPS_BY_CLAUDE */

    // Source Page hidden field overrides location.href if present (set first
    // so deriveSource can use the canonical URL for path matching)
    var sourcePageEl = form.querySelector('input[name="contact[Source Page]"]');
    if (sourcePageEl && sourcePageEl.value) payload.sourceUrl = sourcePageEl.value;

    // Form Subject hidden field doubles as the form's human-readable name.
    // (Source is derived from URL path, not subject — see deriveSource.)
    var formSubjectEl = form.querySelector('input[name="contact[Form Subject]"]');
    var formSubject = formSubjectEl ? formSubjectEl.value : '';
    payload.source = deriveSource(payload.sourceUrl);
    payload.formName = formSubject || (document.title || '').trim();

    // Walk every named input/select/textarea
    var els = form.querySelectorAll('input[name], select[name], textarea[name]');
    var attachmentLinks = [];
    Array.prototype.forEach.call(els, function(el) {
      var rawName = el.getAttribute('name');
      var inner = unwrapName(rawName);
      if (!inner) return; // skip non-contact[] fields (e.g. csrf/form_type)

      // Skip unchecked radios / checkboxes
      if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
      // Skip file inputs (data lives in contact[attachments] hidden field)
      if (el.type === 'file') return;
      // Skip submit buttons
      if (el.type === 'submit' || el.type === 'button') return;

      var key = inner.toLowerCase();
      var val = (el.value || '').trim();
      if (!val) return;

      if (Object.prototype.hasOwnProperty.call(ALIASES, key)) {
        var canonical = ALIASES[key];
        if (canonical === null) return; // explicitly dropped
        if (canonical === 'attachments') {
          // contact[attachments] is a comma/JSON string of uploaded URLs (set by theme JS)
          try {
            var parsed = JSON.parse(val);
            if (Array.isArray(parsed)) attachmentLinks = attachmentLinks.concat(parsed);
            else attachmentLinks.push(parsed);
          } catch (e) {
            val.split(/[,\n]/).map(function(s) { return s.trim(); }).filter(Boolean).forEach(function(s) { attachmentLinks.push(s); });
          }
          return;
        }
        if (canonical === 'residentialOrCommercial') {
          // value comes from the radio's `value` attribute (block.settings.value_yes/no)
          var lower = val.toLowerCase();
          if (lower.indexOf('commercial') !== -1) payload.residentialOrCommercial = 'commercial';
          else if (lower.indexOf('residential') !== -1) payload.residentialOrCommercial = 'residential';
          // Otherwise drop — shape is enum-restricted server-side
          return;
        }
        if (canonical === 'inquiryType') {
          // Storefront /pages/contact populates contact[inquiry_type] with a
          // marketing string ("New order inquiry. Call them in 3, 2, 1...") not
          // the enum value the worker schema expects. Pattern-match instead.
          var lowerIq = val.toLowerCase();
          if (lowerIq.indexOf('new order') !== -1) payload.inquiryType = 'new_order';
          else if (lowerIq.indexOf('existing order') !== -1) payload.inquiryType = 'existing_order';
          else if (lowerIq.indexOf('something else') !== -1) payload.inquiryType = 'something_else';
          // Otherwise drop — schema would reject anything else
          return;
        }
        payload[canonical] = val;
        return;
      }

      // Unknown field — preserve key as-is in responses blob (so reps see
      // the question label verbatim in the email)
      payload.responses[inner] = val;
    });

    if (attachmentLinks.length > 0) payload.attachments = attachmentLinks.slice(0, 10);

    // Parse contact[location] for state + zip + city (drives territory assignment)
    var locField = form.querySelector('input[name="contact[location]"]');
    if (locField && locField.value) {
      var parsed = parseLocation(locField.value);
      if (parsed.state && !payload.state) payload.state = parsed.state;
      if (parsed.zip   && !payload.zip)   payload.zip   = parsed.zip;
      if (parsed.city  && !payload.city)  payload.city  = parsed.city;
    }

    // Default missing required fields with sensible blanks the schema will reject
    if (!payload.name)  payload.name  = '';
    if (!payload.email) payload.email = '';

    return payload;
  }

  // Retry queue — if a /lead-intake POST fails (network error, 5xx, worker
  // restart, transient cf-edge issue), stash the payload in localStorage and
  // retry on the next page load this same browser visits. The customer
  // already saw the thank-you page so they don't know — but the lead will
  // land within minutes once they navigate anywhere else on the site.
  // Server-side dedup (lead-routes.js fingerprint hash) makes the retry
  // safe even if the first POST actually landed and we just didn't get the
  // ACK back. Cap: 5 entries, 3 attempts each, 1h max age.
  var RETRY_QUEUE_KEY = 'bsfs_lead_retry_queue';
  var RETRY_MAX_ENTRIES = 5;
  var RETRY_MAX_ATTEMPTS = 3;
  var RETRY_MAX_AGE_MS = 60 * 60 * 1000;

  function queueFailedLead(payload, reason) {
    try {
      var queue = JSON.parse(localStorage.getItem(RETRY_QUEUE_KEY) || '[]');
      queue.push({ payload: payload, ts: Date.now(), attempts: 0, reason: reason || 'unknown' });
      queue = queue.slice(-RETRY_MAX_ENTRIES);
      localStorage.setItem(RETRY_QUEUE_KEY, JSON.stringify(queue));
      debugWarn('[lead-capture] queued for retry. queue size=', queue.length, 'reason=', reason);
    } catch (e) { debugWarn('[lead-capture] queueFailedLead failed:', e); }
  }

  function flushRetryQueue() {
    var queue;
    try { queue = JSON.parse(localStorage.getItem(RETRY_QUEUE_KEY) || '[]'); } catch (e) { return; }
    if (!Array.isArray(queue) || queue.length === 0) return;
    var now = Date.now();
    // Filter to entries still worth retrying. Increment attempts here so
    // even if the retry races and never resolves, we don't loop forever.
    var fresh = [];
    var inflight = [];
    queue.forEach(function (entry) {
      if (!entry || !entry.payload || !entry.ts) return;
      if (now - entry.ts > RETRY_MAX_AGE_MS) return;
      if ((entry.attempts || 0) >= RETRY_MAX_ATTEMPTS) return;
      entry.attempts = (entry.attempts || 0) + 1;
      inflight.push(entry);
    });
    // Persist the incremented-attempts queue before firing — if the page
    // unloads mid-retry we don't want to lose the attempt count.
    try { localStorage.setItem(RETRY_QUEUE_KEY, JSON.stringify(inflight)); } catch (e) {}
    inflight.forEach(function (entry) {
      try {
        fetch(WORKER_URL, {
          method: 'POST',
          body: JSON.stringify(entry.payload),
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
          mode: 'cors',
          credentials: 'include',
        }).then(function (r) {
          if (r.ok) {
            // Success — drop this entry from the queue.
            try {
              var current = JSON.parse(localStorage.getItem(RETRY_QUEUE_KEY) || '[]');
              current = current.filter(function (e) { return e.ts !== entry.ts; });
              localStorage.setItem(RETRY_QUEUE_KEY, JSON.stringify(current));
              debug('[lead-capture] retry succeeded, dropped from queue. attempts=', entry.attempts);
            } catch (e) {}
          } else if (r.status >= 500) {
            debug('[lead-capture] retry got ' + r.status + ', leaving in queue');
          } else {
            // 4xx — payload is bad, no point retrying. Drop.
            try {
              var current = JSON.parse(localStorage.getItem(RETRY_QUEUE_KEY) || '[]');
              current = current.filter(function (e) { return e.ts !== entry.ts; });
              localStorage.setItem(RETRY_QUEUE_KEY, JSON.stringify(current));
            } catch (e) {}
          }
        }).catch(function () { /* leave in queue; next page load tries again */ });
      } catch (e) {}
    });
  }

  // Flush on page load (after a short delay so we don't compete with the
  // page's critical render path).
  setTimeout(flushRetryQueue, 2000);

  function send(payload) {
    var body = JSON.stringify(payload);
    // fetch with keepalive is the single transport. It survives the page
    // unload triggered by Shopify's native form submit (same guarantee
    // sendBeacon offers) AND yields a real HTTP status visible in DevTools.
    //
    // 2026-05-18 — sendBeacon was previously used as a .catch() fallback,
    // but on the unload transition browsers can deliver the fetch AND
    // reject the promise (the underlying POST succeeded; the abort signal
    // fires anyway). That delivered the same payload twice — see
    // JW-1140-A / JW-1141-A landing 752ms apart from one click. The
    // server now dedupes by submission fingerprint as a hard backstop, so
    // even if a future transport quirk re-introduces a race the duplicate
    // can't land as a separate lead.
    try {
      fetch(WORKER_URL, {
        method: 'POST',
        body: body,
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        mode: 'cors',
        // CUSTOM_MARKETINGOPS_BY_CLAUDE — include so worker can Set-Cookie
        // the bsfs_utm_journey HttpOnly mirror (Safari ITP escape hatch).
        credentials: 'include',
      })
        .then(function(r) {
          debug('[lead-capture] POST status=' + r.status + ' source=' + payload.source);
          // Network-level success but worker rejected with 5xx → queue for retry.
          // 4xx is a determinism failure (bad payload, validation, dedup hit
          // returning success body) — do not retry, would just burn cycles.
          if (r.status >= 500 && r.status <= 599) queueFailedLead(payload, 'http-' + r.status);
        })
        .catch(function(err) {
          debugWarn('[lead-capture] POST failed:', err);
          queueFailedLead(payload, 'network');
        });
    } catch (e) {
      debugWarn('[lead-capture] fetch threw synchronously:', e);
    }
    return true;
  }

  // Visible button feedback on click. Spinner only — no full-page overlay
  // here (that happens during the post-navigation render by theme.liquid's
  // synchronous script). Keeping the click-time treatment off the page
  // body so Turnstile's challenge modal — if it appears — can be clicked
  // through. The sessionStorage flag for the post-navigation overlay is
  // NOT set here anymore; it's set inside finalizeSubmit.go() right before
  // window.location.href fires, so any error path that returns early
  // doesn't leave a stale flag that would re-veil the next page load.
  function showSubmitButtonLoading(form) {
    try {
      var btn = form && (form.querySelector('button[type="submit"]') || form.querySelector('button:not([type])'));
      if (btn && btn.dataset.bsfsBtnLoading !== '1') {
        btn.dataset.bsfsBtnLoading = '1';
        btn.classList.add('bsfs-submitting-btn');
        btn.disabled = true;
        var spin = document.createElement('span');
        spin.className = 'bsfs-submit-spinner';
        spin.setAttribute('aria-hidden', 'true');
        btn.appendChild(spin);
      }
    } catch (e) {}
  }

  // Pages where the form IS the page (request-bulk-quote, talk-to-specialist,
  // etc.) — the user reached them by clicking a CTA on a product/collection
  // page, so "back" should mean "back to where I was BEFORE this form,"
  // not "back to this form." For landing pages with real content (rubber-
  // flooring-for-X) the form is incidental and "back to this page" is right.
  // /pages/contact is excluded entirely from back-links elsewhere (circular).
  var INTERMEDIATE_FORM_PATHS = [
    '/pages/request-bulk-quote',
    '/pages/talk-to-specialist',
    '/pages/energylounger-expert-call',
    '/pages/faqs',
  ];

  // Stash navigation-time state: the post-load overlay flag (timestamped,
  // theme.liquid honors <30s) + the source page URL/title (for the
  // thank-you page's dynamic "Back to ..." CTA). Called inside go() so it
  // only runs if we're actually about to navigate.
  function stashNavigationState() {
    try { sessionStorage.setItem('bsfs_form_submitting', String(Date.now())); } catch (e) {}
    try {
      var sourceUrl = window.location.href;
      var sourceTitle = (document.title || '');
      // Intermediate-form-page override: use the page they came from. Falls
      // through to current-page behavior if no prev was captured (direct
      // entry, deep link, etc.) or if prev is same-path as current (refresh).
      var path = (window.location.pathname || '').toLowerCase().replace(/\/+$/, '');
      if (INTERMEDIATE_FORM_PATHS.indexOf(path) !== -1) {
        try {
          var prev = JSON.parse(sessionStorage.getItem('bsfs_nav_prev') || 'null');
          if (prev && prev.url) {
            var prevUrl = new URL(prev.url);
            if (prevUrl.origin === window.location.origin && prevUrl.pathname.toLowerCase().replace(/\/+$/, '') !== path) {
              sourceUrl = prev.url;
              sourceTitle = prev.title || '';
            }
          }
        } catch (e) {}
      }
      var t = sourceTitle.replace(/\s*[–—\-|]\s*Blue Sky Fit(ness)? Supply\s*$/i, '').trim();
      sessionStorage.setItem('bsfs_form_source_url', sourceUrl);
      sessionStorage.setItem('bsfs_form_source_title', t);
    } catch (e) {}
  }

  // Execute Cloudflare Turnstile (invisible) if available, then proceed with
  // the submit sequence. We give Turnstile up to 8 seconds to produce a
  // token — covers normal challenges + the rare visible-challenge case where
  // a user has to click. If Turnstile's script never loaded (CDN blocked, ad
  // blocker, very slow network) we skip and proceed; the worker logs the
  // skip in audit + still creates the lead (fail-open v1). Once we've kicked
  // the POSTs we navigate IMMEDIATELY — both fetches use keepalive:true so
  // they finish over the wire after the page unloads.
  function finalizeSubmit(form, payload) {
    var navigated = false;
    function go(reason) {
      if (navigated) {
        debug('[lead-capture] go() called again (' + reason + '), already navigated, skipping');
        return;
      }
      navigated = true;
      /* CUSTOM_BLOG_CTA_BY_CLAUDE — inline forms (e.g. the mid-article blog CTA)
         stay on the page and surface their own confirmation instead of redirecting
         to /pages/thank-you. Everything upstream is identical — same payload,
         Turnstile, attribution and retry — only the post-send UX differs. The
         form opts in via data-bsfs-inline="true" (set by blog-lead-cta.js). */
      if (form.dataset.bsfsInline === 'true') {
        debug('[lead-capture] go() inline (' + reason + '): send + fire success event');
        send(payload);
        try { form.dispatchEvent(new CustomEvent('bsfs:lead-success', { bubbles: true })); } catch (e) {}
        return;
      }
      debug('[lead-capture] go() proceeding (' + reason + '): send + navigate');
      stashNavigationState();
      send(payload);
      debug('[lead-capture] navigating to /pages/thank-you?source=contact');
      window.location.href = '/pages/thank-you?source=contact';
    }
    var widget = document.querySelector('.bsfs-turnstile');
    var hasTurnstile = (typeof window.turnstile !== 'undefined' && widget);
    debug('[lead-capture] finalizeSubmit: hasTurnstile=', hasTurnstile, 'widget=', !!widget, 'turnstile API=', typeof window.turnstile);
    if (!hasTurnstile) {
      go('no-turnstile');
      return;
    }
    // Hard fallback timer — if Turnstile doesn't fire either callback in
    // 8s we go anyway. Common cause: user is on a network that throttles
    // challenges.cloudflare.com (rare for residential, slightly more
    // common on corporate VPNs).
    var fallback = setTimeout(function () { go('fallback-timer-8s'); }, 8000);
    try {
      debug('[turnstile] calling execute() on widget');
      window.turnstile.execute(widget, {
        callback: function (token) {
          debug('[turnstile] success callback, token len=', token && token.length);
          clearTimeout(fallback);
          payload.cfTurnstileResponse = token;
          go('turnstile-success');
        },
        'error-callback': function (errCode) {
          debugWarn('[turnstile] execute error:', errCode);
          clearTimeout(fallback);
          go('turnstile-error');
        },
        'timeout-callback': function () {
          debugWarn('[turnstile] timeout-callback');
          clearTimeout(fallback);
          go('turnstile-timeout');
        },
      });
    } catch (err) {
      debugWarn('[turnstile] execute threw:', err);
      clearTimeout(fallback);
      go('execute-threw');
    }
  }

  // (Removed fireShopifyContactPost — formerly fire-and-forget POST to
  // Shopify's /contact endpoint for an info@ email backup. The POST was
  // uncaptcha'd from the client, so Shopify rejected it every time with
  // a 302 to /challenge. No emails were ever delivered via this path. Dead
  // code, removed during 2026-05-19 self-audit. Lead delivery is now
  // exclusively via rfq-worker → Resend. Audit also added a localStorage
  // retry queue (see queueFailedLead / flushRetryQueue) to close the silent-
  // loss gap that this removal exposes.)

  document.addEventListener('submit', function(e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') {
      debug('[lead-capture] non-form submit, ignoring');
      return;
    }
    // Only Shopify contact forms (action ends with /contact and posts contact[...] fields)
    var action = (form.getAttribute('action') || '').toLowerCase();
    debug('[lead-capture] submit event fired. form id=', form.id, 'action=', action);
    if (action.indexOf('/contact') === -1) {
      debug('[lead-capture] action does not contain /contact, ignoring');
      return;
    }
    if (form.dataset.bsfsLeadIntakeFired === 'true') {
      debug('[lead-capture] already fired this submission, returning');
      return; // belt-and-suspenders
    }

    try {
      var payload = buildPayload(form);
      debug('[lead-capture] payload built, email=', payload.email, 'source=', payload.source);
      // Without an email the rfq-worker schema would reject the lead; bail
      // and let the browser's native HTML5 validation surface the missing
      // required field. We DO NOT continue and rely on a Shopify email
      // backup anymore — that path is gone now that we preventDefault below.
      if (!payload.email) {
        debug('[lead-capture] no email in payload, allowing native submit so HTML5 validation surfaces it');
        return;
      }
      form.dataset.bsfsLeadIntakeFired = 'true';
      // Visual feedback on the button (not a full-page overlay yet, so the
      // CAPTCHA modal would still pop up cleanly if we were doing native
      // submit — but we aren't anymore, see below).
      showSubmitButtonLoading(form);
      debug('[lead-capture] calling preventDefault + finalizeSubmit');
      /* CUSTOM_FORM_SUBMIT_OVERLAY_BY_CLAUDE + CUSTOM_TURNSTILE_BY_CLAUDE.
         Bypass native form, fire /lead-intake, navigate to thank-you. The
         /lead-intake POST uses fetch keepalive so it survives the navigation.
           1. Run Cloudflare Turnstile (invisible, executes on demand)
           2. send(payload + cfTurnstileResponse) → /lead-intake (rfq-worker)
           3. window.location.href                → /pages/thank-you?source=contact
         Turnstile gives the worker a token to verify against
         challenges.cloudflare.com/turnstile/v0/siteverify — replaces the
         bot-detection we lost by bypassing Shopify's hCaptcha. emailHash is
         dropped (was async; would have required awaiting). If Turnstile JS
         hasn't loaded (CDN blip, CF blocked) we proceed without a token —
         worker logs as "turnstile-skipped" and still creates the lead
         (fail-open by default; flip TURNSTILE_STRICT env var on rfq-worker
         to force fail-closed once spam metrics warrant it). */
      e.preventDefault();
      finalizeSubmit(form, payload);
      /* END CUSTOM_FORM_SUBMIT_OVERLAY_BY_CLAUDE + CUSTOM_TURNSTILE_BY_CLAUDE */
    } catch (err) {
      // Anything failed pre-navigation — fall back to letting the native
      // form submit do its thing (no preventDefault path) so the customer's
      // submission isn't black-holed.
      debugWarn('[lead-capture] interceptor failed, falling back to native submit:', err);
    }
  }, true);
})();
